// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import {
  type BrandVariants,
  createDarkTheme,
  createLightTheme,
  type Theme,
} from "@fluentui/react-components";

const crimsonBrand: BrandVariants = {
  10: "#160004",
  20: "#350010",
  30: "#54001b",
  40: "#730028",
  50: "#910036",
  60: "#b00043",
  70: "#ca0039",
  80: "#dc143c",
  90: "#e63e60",
  100: "#eb6277",
  110: "#f0858e",
  120: "#f4a1a9",
  130: "#f7bcc3",
  140: "#fad5d9",
  150: "#fce8eb",
  160: "#fef3f4",
};

export const crimsonTheme: Theme = {
  ...createLightTheme(crimsonBrand),
  fontFamilyBase: '"Sora Variable", "Segoe UI", sans-serif',
  fontFamilyMonospace: '"IBM Plex Mono", Consolas, monospace',
  colorBrandBackground: "#dc143c",
  colorBrandBackgroundHover: "#c9003a",
  colorBrandBackgroundPressed: "#b00035",
  colorNeutralBackground1: "#fff8f9",
  colorNeutralBackground2: "#fff0f2",
  colorNeutralBackground3: "#ffe6ea",
  colorNeutralForeground1: "#1a0008",
  colorNeutralForeground2: "#5c1520",
  shadow4: "0 20px 40px rgba(180, 0, 50, 0.16)",
  shadow8: "0 28px 56px rgba(140, 0, 40, 0.20)",
  borderRadiusLarge: "16px",
  borderRadiusXLarge: "20px",
};

const oceanBrand: BrandVariants = {
  10: "#060a16",
  20: "#101b3a",
  30: "#16265a",
  40: "#1c3275",
  50: "#214091",
  60: "#2750ad",
  70: "#2d5fc9",
  80: "#386de0",
  90: "#4e7cec",
  100: "#628af4",
  110: "#7698f8",
  120: "#89a6fb",
  130: "#9cb3fd",
  140: "#afc1fe",
  150: "#c2ceff",
  160: "#d6ddff",
};

export const oceanTheme: Theme = {
  ...createLightTheme(oceanBrand),
  fontFamilyBase: '"Sora Variable", "Segoe UI", sans-serif',
  fontFamilyMonospace: '"IBM Plex Mono", Consolas, monospace',
  colorBrandBackground: "#214091",
  colorBrandBackgroundHover: "#1c3275",
  colorBrandBackgroundPressed: "#16265a",
  colorNeutralBackground1: "#f7f9ff",
  colorNeutralBackground2: "#eef3ff",
  colorNeutralBackground3: "#e6edff",
  colorNeutralForeground1: "#0f1730",
  colorNeutralForeground2: "#324067",
  shadow4: "0 20px 40px rgba(19, 34, 84, 0.18)",
  shadow8: "0 28px 56px rgba(10, 26, 80, 0.22)",
  borderRadiusLarge: "16px",
  borderRadiusXLarge: "20px",
};

const darkThemeOverrides = {
  colorStatusDangerForeground1: "#f1707b",
  fontFamilyBase: '"Sora Variable", "Segoe UI", sans-serif',
  fontFamilyMonospace: '"IBM Plex Mono", Consolas, monospace',
  borderRadiusLarge: "16px",
  borderRadiusXLarge: "20px",
};

export const crimsonDarkTheme: Theme = {
  ...createDarkTheme(crimsonBrand),
  ...darkThemeOverrides,
};

export const oceanDarkTheme: Theme = {
  ...createDarkTheme(oceanBrand),
  ...darkThemeOverrides,
  colorBrandForeground1: oceanBrand[110],
  colorBrandForegroundLink: oceanBrand[110],
  colorBrandForegroundLinkHover: oceanBrand[120],
};

export type ThemeName = "crimson" | "ocean";

export type ColorScheme = "light" | "dark";

export const appThemes: Record<ThemeName, Theme> = {
  crimson: crimsonTheme,
  ocean: oceanTheme,
};

export const appDarkThemes: Record<ThemeName, Theme> = {
  crimson: crimsonDarkTheme,
  ocean: oceanDarkTheme,
};

export function getAppTheme(name: ThemeName, scheme: ColorScheme): Theme {
  return scheme === "dark" ? appDarkThemes[name] : appThemes[name];
}

export const themeMetadata: Record<
  ThemeName,
  { label: string; description: string; accentHex: string; bgHex: string }
> = {
  crimson: {
    label: "Crimson",
    description: "Carmine and crimson red accent",
    accentHex: "#dc143c",
    bgHex: "#fff8f9",
  },
  ocean: {
    label: "Ocean",
    description: "Deep ocean blue accent",
    accentHex: "#386de0",
    bgHex: "#f7f9ff",
  },
};
